import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import StepIndicator from "./StepIndicator";
import StepNavigation from "./StepNavigation";
import BioData from "../BioData";
import Referee from "../Referee";
import OLevel from "../OLevel";
import ALevel from "../ALevel";
import Photo from "../Photo";
import PayFee from "../PayFee";
import QuickApplyDocs from "./QuickApplyDocs";
import QuickApplySubmit from "./QuickApplySubmit";

const TOTAL_STEPS = 8;

const STEPS = [
  { label: "Bio Data", key: "bio" },
  { label: "Parents", key: "ref" },
  { label: "O-Level", key: "olevel" },
  { label: "A-Level", key: "alevel" },
  { label: "Passport", key: "photo" },
  { label: "Payment", key: "pay" },
  { label: "Documents", key: "docs" },
  { label: "Submit", key: "submit" },
];

const QuickApply = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [docsComplete, setDocsComplete] = useState(false);
  const [modeOfEntry, setModeOfEntry] = useState("");

  // Generate or preserve session ID
  const quickApplySessionId = useMemo(() => {
    return searchParams.get("sid") || crypto.randomUUID();
  }, []);

  const rawStep = parseInt(searchParams.get("step") || "1", 10);
  const currentStep = Math.max(1, Math.min(TOTAL_STEPS, isNaN(rawStep) ? 1 : rawStep));

  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboardDataset"],
    queryFn: async () => {
      const req = await axiosget("ms_dashboard");
      return req?.data;
    },
    refetchOnWindowFocus: true,
  });

  const isStepComplete = useCallback(
    (key) => {
      if (!data || data?.type) return false;
      switch (key) {
        case "bio":
          return data.bio >= 1;
        case "ref":
          return data.ref >= 2;
        case "olevel":
          return data.olevel >= 1 && data.result >= 6;
        case "alevel":
          return true; // optional, always passable
        case "photo":
          return Boolean(data.photo);
        case "pay":
          return Boolean(data.pay);
        case "docs":
          return docsComplete;
        case "submit":
          return false; // final step
        default:
          return false;
      }
    },
    [data, docsComplete]
  );

  const stepsWithStatus = useMemo(
    () => STEPS.map((s) => ({ ...s, isComplete: isStepComplete(s.key) })),
    [isStepComplete]
  );

  const setStep = useCallback(
    (n) => {
      const clamped = Math.max(1, Math.min(TOTAL_STEPS, n));
      setSearchParams({ step: String(clamped), sid: quickApplySessionId }, { replace: false });
    },
    [setSearchParams, quickApplySessionId]
  );

  // Refetch dashboard data when step changes so Next button reflects latest state
  React.useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["dashboardDataset"] });
  }, [currentStep, queryClient]);

  // Ensure sid is in URL on mount
  React.useEffect(() => {
    if (!searchParams.get("sid")) {
      setSearchParams({ step: String(currentStep), sid: quickApplySessionId }, { replace: true });
    }
  }, []);

  // Render the active step component
  const renderActiveStep = () => {
    switch (currentStep) {
      case 1: return <BioData wizardMode={true} />;
      case 2: return <Referee wizardMode={true} />;
      case 3: return <OLevel wizardMode={true} />;
      case 4: return <ALevel wizardMode={true} />;
      case 5: return <Photo wizardMode={true} />;
      case 6: return <PayFee wizardMode={true} quickApplySessionId={quickApplySessionId} />;
      case 7: return (
        <QuickApplyDocs
          quickApplySessionId={quickApplySessionId}
          onCompleteChange={setDocsComplete}
          modeOfEntry={modeOfEntry}
          onModeOfEntryChange={setModeOfEntry}
        />
      );
      case 8: return (
        <QuickApplySubmit
          quickApplySessionId={quickApplySessionId}
          modeOfEntry={modeOfEntry}
        />
      );
      default: return null;
    }
  };

  if (isLoading) return <Loader />;

  if (error) {
    return (
      <div className="px-4 py-4">
        <Alert variant="danger">
          <AlertDescription>{`Error loading dashboard: ${error}`}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50/30 pb-20">
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="pt-4 pb-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-neutral-900">Quick Apply</h1>
              <p className="text-sm text-neutral-500">Complete all steps to submit your application</p>
            </div>
            <button
              onClick={() => navigate("/app/dashboard")}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        {/* Step Indicator */}
        <StepIndicator
          steps={stepsWithStatus}
          currentStep={currentStep}
          onStepClick={setStep}
        />

        {/* Active Step Content */}
        <div className="mt-2">
          {renderActiveStep()}
        </div>
      </div>

      {/* Navigation Footer */}
      <StepNavigation
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        currentStepComplete={stepsWithStatus[currentStep - 1]?.isComplete}
        onBack={() => setStep(currentStep - 1)}
        onNext={() => setStep(currentStep + 1)}
      />
    </div>
  );
};

export default QuickApply;
